import ClickableLink from 'components/Common/Clickable/ClickableLink'
import Image from 'components/Common/Image/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import PaletteSheet from 'LuxKit/PaletteSheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import noop from 'lib/function/noop'
import Group from 'components/utils/Group'
import { isString } from 'lib/string/stringUtils'
import { stripMarkdown } from 'lib/string/stripMarkdown'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import clsx from 'clsx'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import useHasMounted from 'hooks/useHasMounted'
import getResizableImage from '../getResizableImage'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'
import WishlistGuard from 'wishlists/components/WishlistGuard'

const CarouselCardContainer = styled(ClickableLink)`
  display: grid;
  grid-template-rows: auto 1fr;
  gap: 0;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};

  &.growth-tile-no-border {
    border-color: transparent;
    background-clip: padding-box;
  }
  border-radius: ${rem(12)};
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  min-width: 0;
  height: 100%;
  position: relative;
`

const ImageContainer = styled(PaletteSheet)`
  position: relative;
  aspect-ratio: 16 / 9;
  width: 100%;
  overflow: hidden;

  > picture {
    display: block;
    width: 100%;
    height: 100%;
  }

  &.hover {
    > :nth-child(2) {
      position: absolute;
      inset: 0;
      transition: opacity 0.3s;
      opacity: 0;
    }

    > :nth-child(1) {
      transition: opacity 0.3s;
      opacity: 1;
    }

    ${mediaHoverable} {
      &:hover {
        > :nth-child(1) {
          opacity: 0;
        }

        > :nth-child(2) {
          opacity: 1;
        }
      }
    }
  }
`

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-columns: auto auto;
  grid-template-areas: "left right";
  grid-template-rows: 1fr;
  padding: ${rem(16)};
  gap: ${rem(12)};
  pointer-events: none;
  justify-content: space-between;

  & > .left {
    justify-self: flex-start;
    align-self: flex-start;
    grid-area: left;
  }

  & > .right {
    justify-self: flex-end;
    align-self: flex-start;
    grid-area: right;
  }

  a, button {
    pointer-events: auto;
  }
`

const IMAGE_ASPECT_RATIO = '16:9'
const IMAGE_WIDTH = 320
const IMAGE_HEIGHT = 180

function getPlainSummaryDescription(description: string) {
  return stripMarkdown(description.replace(/!\[[^\]]*\]\([^)]*\)/g, ''))
    .replace(/https?:\/\/\S+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

const DetailsContainer = styled(Group)`
  min-width: 0;
  overflow: hidden;
  padding: ${rem(16)};
`

// Allow long locations to truncate beside the review score.
const Location = styled(Group)`
  min-width: 0;
`

interface Props extends Utils.PropsWithDataAttributes {
  className?: string
  bookmarkButton?: React.ReactElement
  description?: string | React.ReactElement
  descriptionLines?: number
  image: App.Image
  location: string | React.ReactElement
  providerName?: string
  title: string
  priceDetails?: React.ReactElement
  to: string
  onClick?: (isRightClick?: boolean) => void
  rating?: App.OfferRating
  urgencyLabels?: React.ReactElement
  partnershipLabel?: React.ReactElement
  imageCornerLabel?: React.ReactElement
  images?: Array<App.Image>
  taglineInclusions?: React.ReactElement
  /**
   * @default default
   */
  variant?: 'default' | 'opaque'
  /**
   * Whether or not the offer tile should load it's image without
   * waiting for the tile to be in view.
   */
  eagerLoadImage?: boolean
}

function GrowthCarouselCardSmall({
  bookmarkButton,
  description,
  descriptionLines,
  image: baseImage,
  images,
  location,
  providerName,
  title,
  priceDetails,
  to,
  onClick = noop,
  rating,
  urgencyLabels,
  partnershipLabel,
  className,
  imageCornerLabel,
  taglineInclusions,
  variant = 'default',
  eagerLoadImage,
  ...rest
}: Props) {
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()
  const handleImageLoaded = useCallback(() => {
    dispatchOfferListEvent({ type: OfferListEvents.offerReady })
  }, [dispatchOfferListEvent])

  const hoverImage = images?.[1]
  const isDesktop = useScreenSizeUp('tablet')
  const isMounted = useHasMounted()
  const isHoverEnabled = isMounted && isDesktop && !!hoverImage

  const image = useMemo(() => getResizableImage(baseImage ?? images?.[0]), [baseImage, images])
  const resizedHoverImage = useMemo(() => getResizableImage(hoverImage), [hoverImage])
  const plainDescription = useMemo(() => {
    return isString(description) ? getPlainSummaryDescription(description) : undefined
  }, [description])

  const handleClick = useCallback(() => {
    onClick()
  }, [onClick])

  const handleContextMenu = useCallback(() => {
    onClick(true)
  }, [onClick])

  return <CarouselCardContainer
    {...rest}
    onClick={handleClick}
    onContextMenu={handleContextMenu}
    target={isOpenNewTabOfferClickEnabled ? '_blank' : undefined}
    to={to}
    className={clsx(className, `variant-${variant}`)}
  >
    <ImageContainer
      className={clsx({ hover: isHoverEnabled })}
      paletteType="default"
    >
      <Image
        aspectRatio={IMAGE_ASPECT_RATIO}
        image={image}
        fit="center"
        dpr={2}
        height={IMAGE_HEIGHT}
        width={IMAGE_WIDTH}
        onLoad={handleImageLoaded}
        loading={eagerLoadImage ? 'eager' : 'lazy'}
        fetchPriority={eagerLoadImage ? 'high' : undefined}
      />
      {resizedHoverImage && isHoverEnabled && (
        <Image
          aspectRatio={IMAGE_ASPECT_RATIO}
          image={resizedHoverImage}
          fit="center"
          dpr={2}
          height={IMAGE_HEIGHT}
          width={IMAGE_WIDTH}
          loading="lazy"
        />
      )}
      <ImageOverlay>
        <LabelGroup className="left">
          {imageCornerLabel}
        </LabelGroup>
        <WishlistGuard>
          <div className="right">
            {bookmarkButton}
          </div>
        </WishlistGuard>
      </ImageOverlay>
    </ImageContainer>
    <DetailsContainer
      direction="vertical"
      gap={12}
      className={`variant-${variant}`}
    >
      {/* Inclusion previews may fill spare height without moving the pricing row. */}
      <Group direction="vertical" gap={12} grow>
        <Group direction="vertical" gap={4}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
            <Location direction="horizontal" gap={4} verticalAlign="center" grow>
              {isString(location) && !!location.trim() && <>
                <LineMapMarkerIcon size="XS" />
                <Caption colour="light-foreground-alt" variant="medium" lineClamp={1}>{location}</Caption>
              </>}
              {!isString(location) && location}
            </Location>
            {rating && <OfferRating
              variant="xs"
              rating={rating}
              onlyNumber
              inlineLabel
              hideLink
            />}
          </Group>
          {/* Keep the property name above the marketing headline, per product review. */}
          {providerName && <BodyText variant="medium">{providerName}</BodyText>}
          <BodyText lineClamp={2} variant="medium" weight="bold">{title}</BodyText>
        </Group>
        {plainDescription && <BodyText colour="light-foreground-alt" lineClamp={descriptionLines || 2} variant="medium" wrap="word-wrap">{plainDescription}</BodyText>}
        {description && !isString(description) && description}
        {taglineInclusions}
      </Group>
      <Group direction="vertical" gap={12}>
        {/* Product review retains existing tag eligibility and count; no Figma one-label cap. */}
        <LabelGroup>{partnershipLabel}{urgencyLabels}</LabelGroup>
        {priceDetails}
      </Group>
    </DetailsContainer>
  </CarouselCardContainer>
}

export default GrowthCarouselCardSmall
